import React from 'react';
import PropTypes from 'prop-types';
import { ContentSection } from '~components/Layout';
import DeadlineRelative from '~components/common/DeadlineRelative';
import { formatDateFriendly } from '~utils/formatDate';
import { registrationNotRequired } from '~components/Register/specialCases';
import {
  getAbsenteeMailLink,
  getRegistrationForm,
} from '~utils/faqAnswerUtils';
import { CustomizableLink } from '~components/common/Link';
import {
  printMailDisabled,
  onlinePortalMustPrintApplication,
} from '~components/Absentee/navigateToSecondStep';

function StateDeadlinesSection({ state, stateInfo, electionDate }) {
  const displayRegistrationDeadlines = !registrationNotRequired.includes(
    state.code
  );
  const onlineRegistrationAvailable =
    stateInfo.registration_methods.multiselect_value.includes('online');
  const absenteeOnlinePortalMustPrintApplication =
    onlinePortalMustPrintApplication.includes(state.code);
  const onlineAbsenteeRequestAvailable =
    stateInfo.absentee_request_methods.multiselect_value.includes('online') &&
    !absenteeOnlinePortalMustPrintApplication;
  const absenteePrintMailDisabled = printMailDisabled.includes(state.code);
  const inPersonBallotReturnAvailable =
    stateInfo.ballot_return_locations.multiselect_value.length > 0 &&
    stateInfo.ballot_return_locations.text !== '[]';
  const earlyVotingAvailable =
    stateInfo.early_voting_starts.text &&
    stateInfo.early_voting_starts.text !== 'N/A';
  const hasElectionDayRegistration =
    stateInfo.has_sdr_election_day.bool_value === true;
  const hasEarlyVotingRegistration =
    stateInfo.has_sdr_early_voting.bool_value === true;

  const registrationFormUrl = getRegistrationForm(stateInfo, state.code);
  const absenteeMailRequestUrl = getAbsenteeMailLink(stateInfo, state.code);
  const pollingPlaceLocatorUrl =
    stateInfo.gov_tool_polling_place.text ?? '/where-to-vote/';

  return (
    <ContentSection width='mid' className='bg-base-200'>
      <a id='deadlines' aria-hidden='true' />
      <h2>Key deadlines in {state.name}</h2>
      <a id='deadlines-register' aria-hidden='true' />
      <h3 className='mb-4 text-lg font-bold'>Voter registration deadlines</h3>
      {displayRegistrationDeadlines ? (
        <ul className='last:mb-0'>
          {onlineRegistrationAvailable && (
            <li>
              <CustomizableLink
                href={stateInfo.gov_tool_registration.text}
                title={`${state.name} online voter registration website`}
              >
                Online
              </CustomizableLink>
              :{' '}
              <DeadlineRelative
                relativeDeadline={stateInfo.registration_deadline_online}
              />
            </li>
          )}
          <li>
            <CustomizableLink
              href={registrationFormUrl}
              title={`Download ${state.name} paper voter registration form`}
            >
              By mail:
            </CustomizableLink>{' '}
            <DeadlineRelative
              relativeDeadline={stateInfo.registration_deadline_mail}
            />
          </li>
          <li>
            <CustomizableLink
              href={stateInfo.source_registration_info.text}
              title={`${state.name} voter registration information`}
            >
              In person:
            </CustomizableLink>{' '}
            <DeadlineRelative
              relativeDeadline={stateInfo.registration_deadline_in_person}
            />{' '}
            {hasElectionDayRegistration && '(via same-day registration)'}
            {!hasElectionDayRegistration &&
              hasEarlyVotingRegistration &&
              '(via registration during the early voting period)'}
          </li>
        </ul>
      ) : (
        <p>
          {state.name} does not have voter registration. Just bring your ID to
          the polls to vote.
        </p>
      )}

      <a id='deadlines-absentee-request' aria-hidden='true' />
      <h3 className='mb-4 text-lg font-bold'>
        Absentee ballot request deadlines
      </h3>
      <ul className='last:mb-0'>
        {onlineAbsenteeRequestAvailable && (
          <li>
            <CustomizableLink
              href={stateInfo.gov_tool_absentee_request.text}
              title={`${state.name} online ballot request website`}
            >
              Online
            </CustomizableLink>
            :{' '}
            <DeadlineRelative
              relativeDeadline={stateInfo.absentee_deadline_online}
            />
          </li>
        )}
        <li>
          <CustomizableLink
            href={absenteeMailRequestUrl}
            title={
              absenteePrintMailDisabled
                ? 'Contact your state to request a form'
                : `Get the ${state.name} paper ballot request form`
            }
          >
            By mail:
          </CustomizableLink>{' '}
          <DeadlineRelative
            relativeDeadline={stateInfo.absentee_deadline_mail}
          />
        </li>
        <li>
          <CustomizableLink
            href={stateInfo.source_absentee_info.text}
            title={`${state.name} absentee ballot information`}
          >
            In person:
          </CustomizableLink>{' '}
          <DeadlineRelative
            relativeDeadline={stateInfo.absentee_deadline_in_person}
          />
        </li>
      </ul>
      <a id='deadlines-absentee-return' aria-hidden='true' />
      <h3 className='mb-4 text-lg font-bold'>
        Absentee ballot return deadlines
      </h3>
      <ul className='last:mb-0'>
        <li>
          <CustomizableLink
            href={`/local-election-offices/${state.code}`}
            title='Look up your local election office'
            isExternal={false}
          >
            By mail:
          </CustomizableLink>{' '}
          <DeadlineRelative
            relativeDeadline={stateInfo.ballot_return_deadline_mail}
          />
        </li>
        {inPersonBallotReturnAvailable && (
          <li>
            <CustomizableLink
              href={`/local-election-offices/${state.code}`}
              title='Look up your local election office'
              isExternal={false}
            >
              In person:
            </CustomizableLink>{' '}
            <DeadlineRelative
              relativeDeadline={stateInfo.ballot_return_deadline_in_person}
            />
          </li>
        )}
      </ul>
      {(earlyVotingAvailable || electionDate) && (
        <>
          <a id='deadlines-voting' aria-hidden='true' />
          <h3 className='mb-4 text-lg font-bold'>Voting dates</h3>
          <ul className='last:mb-0'>
            {earlyVotingAvailable && (
              <>
                <li>
                  <CustomizableLink
                    href={pollingPlaceLocatorUrl}
                    title={`${state.name} polling place lookup site`}
                  >
                    Early voting starts:
                  </CustomizableLink>{' '}
                  <DeadlineRelative
                    relativeDeadline={stateInfo.early_voting_starts}
                  />
                </li>
                <li>
                  <CustomizableLink
                    href={pollingPlaceLocatorUrl}
                    title={`${state.name} polling place lookup site`}
                  >
                    Early voting ends:
                  </CustomizableLink>{' '}
                  <DeadlineRelative
                    relativeDeadline={stateInfo.early_voting_ends}
                  />
                </li>
              </>
            )}
            {electionDate && (
              <li>
                <CustomizableLink
                  href={pollingPlaceLocatorUrl}
                  title={`${state.name} polling place lookup site`}
                >
                  Election Day:
                </CustomizableLink>{' '}
                {formatDateFriendly(electionDate)}
              </li>
            )}
          </ul>
        </>
      )}
    </ContentSection>
  );
}

StateDeadlinesSection.propTypes = {
  state: PropTypes.object.isRequired,
  stateInfo: PropTypes.object.isRequired,
  electionDate: PropTypes.instanceOf(Date),
};

export default StateDeadlinesSection;
